import {Button, DataStatus, showNotification} from 'platform/components';
import {HStack, Show, VStack} from 'platform/foundation';

import {useMemo, useState} from 'react';

import {isNilOrEmpty, isNotNil} from 'ramda-adjunct';

import {useGetDefinitionTemplateListQuery} from '@dms/api/core';
import {useGetCustomerV2Query} from '@dms/api/customer';
import {
  BulkPrintDocumentKey,
  GetServiceOrderBulkPrintDocumentsRestrictionsApiResponse,
  useGetMetadaServiceCaseQuery,
  useGetServiceOrderAvailableDocumentsQuery,
  useGetServiceOrderBulkPrintDocumentsRestrictionsQuery,
  usePostServiceOrderBulkPrintDocumentsMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {handleApiError, printPDF, useFileNotification, useTenant} from '@dms/shared';

import {isNotNilOrEmpty, isValidUrl, Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {DocumentRow} from './DocumentRow';
import {GenerationErrorBanner} from './GenerationErrorBanner';
import {GenerationResultRow} from './GenerationResultRow';
import {getBulkPrintDocumentRestrictionMessage} from './getBulkPrintDocumentRestrictionMessage';
import {getSingleGeneratedDocument, GenerationResult} from './getSingleGeneratedDocument';
import {groupTemplateOptionsByLocale} from './groupTemplateOptionsByLocale';
import {SelectAllHeader} from './SelectAllHeader';
import {useDocumentSelections} from './useDocumentSelections';

const DOCUMENT_LABEL_MAP: Record<BulkPrintDocumentKey, string> = {
  'order-list': 'entity.order.documents.order-list',
  'job-card': 'entity.order.documents.job-card',
  'power-of-attorney': 'entity.order.documents.power-of-attorney',
  'tire-storage-form': 'entity.order.documents.tire-storage-form',
  'order-summary': 'entity.order.documents.order-summary',
  offer: 'entity.order.documents.offer',
  'claim-handling-protocol': 'entity.order.actions.claim-handling-protocol',
  'claim-resolution-protocol': 'entity.order.actions.claim-resolution-protocol',
};

function getRestrictionMessage(
  restrictions: GetServiceOrderBulkPrintDocumentsRestrictionsApiResponse | undefined,
  documentKey: BulkPrintDocumentKey
) {
  const restriction = restrictions?.find((r) => r?.documentKey === documentKey);
  return getBulkPrintDocumentRestrictionMessage(restriction?.restriction);
}

interface GenerateDocumentsDialogProps extends RequiredTestIdProps {
  serviceCaseId: string;
  serviceOrderId: string;
  onClose: () => void;
  refreshDocuments: () => void;
}

export function GenerateDocumentsDialog(props: GenerateDocumentsDialogProps) {
  const [generationResults, setGenerationResults] = useState<Record<string, GenerationResult>>({});
  const [generationFailures, setGenerationFailures] = useState<Record<string, string[]>>({});

  const [postBulkPrintDocuments, {isLoading: isBulkPrintLoading}] =
    usePostServiceOrderBulkPrintDocumentsMutation();

  const fileNotification = useFileNotification({timeoutMs: 30000});

  const {
    data: availableDocuments,
    isLoading: isAvailableDocumentsLoading,
    isError: isAvailableDocumentsError,
  } = useGetServiceOrderAvailableDocumentsQuery({
    serviceCaseId: props.serviceCaseId,
    serviceOrderId: props.serviceOrderId,
  });

  const {
    data: restrictions,
    isLoading: isRestrictionsLoading,
    isError: isRestrictionsError,
  } = useGetServiceOrderBulkPrintDocumentsRestrictionsQuery({
    serviceCaseId: props.serviceCaseId,
    serviceOrderId: props.serviceOrderId,
  });

  const {isMultiLocale, tenant} = useTenant();

  const {data: definitionTemplateList} = useGetDefinitionTemplateListQuery(
    {},
    {skip: !isMultiLocale}
  );

  const {data: serviceCase} = useGetMetadaServiceCaseQuery(
    {serviceCaseId: props.serviceCaseId},
    {skip: !isMultiLocale}
  );

  const {data: customer} = useGetCustomerV2Query(
    {customerId: serviceCase?.customerId ?? ''},
    {skip: !isMultiLocale || isNilOrEmpty(serviceCase?.customerId)}
  );

  const localeByTemplateId = useMemo(() => {
    const map: Record<string, string | Nullish> = {};
    for (const definition of definitionTemplateList?.definitions ?? []) {
      for (const template of definition.templates) {
        map[template.id] = template.locale;
      }
    }
    return map;
  }, [definitionTemplateList]);

  const preferredLocale = customer?.preferredLocale ?? tenant?.defaultLocale;

  const generatedKeys = new Set(Object.keys(generationResults));

  const {
    selections,
    selectableDocuments,
    visibleDocuments,
    isAllSelected,
    hasSelectedDocuments,
    getSelection,
    getTemplateOptions,
    handleCheckboxChange,
    handleTemplateChange,
    handleSelectAll,
    handleUnselectAll,
  } = useDocumentSelections(availableDocuments, generatedKeys);

  const getTemplateOptionsByLocale = (documentKey: BulkPrintDocumentKey) => {
    const templateOptions = getTemplateOptions(documentKey);

    return isMultiLocale
      ? groupTemplateOptionsByLocale(templateOptions, localeByTemplateId, preferredLocale)
      : templateOptions;
  };

  const handleGenerate = () => {
    const body = selectableDocuments
      .filter((doc) => selections[doc.key]?.isSelected)
      .map((doc) => ({
        key: doc.key,
        templateId: selections[doc.key]?.templateId,
      }));

    postBulkPrintDocuments({
      serviceCaseId: props.serviceCaseId,
      serviceOrderId: props.serviceOrderId,
      body,
    })
      .unwrap()
      .then((response) => {
        const newResults: Record<string, GenerationResult> = {};
        const newFailures: Record<string, string[]> = {};

        for (const item of response) {
          if (isNotNil(item.documentKey)) {
            const key = item.documentKey;

            const pdfUrl = item.printout?.pdfUrl;
            const fileId = item.printout?.fileId;

            if (isNotNilOrEmpty(pdfUrl) && isValidUrl(pdfUrl)) {
              const parsedUrl = new URL(pdfUrl);
              parsedUrl.searchParams.set('type', 'inline');
              newResults[key] = {key, url: parsedUrl.toString(), fileId: fileId ?? ''};
            } else if (isNotNilOrEmpty(pdfUrl) && !isValidUrl(pdfUrl)) {
              newFailures[key] = [i18n.t('entity.order.notifications.invalid-document-url')];
            } else {
              const restrictionMessage = getBulkPrintDocumentRestrictionMessage(item.restriction);

              // eslint-disable-next-line max-depth -- Pre-existing violation
              if (isNotNil(restrictionMessage)) {
                newFailures[key] = restrictionMessage;
              } else {
                newFailures[key] = [
                  i18n.t('entity.order.notifications.document-generation-failed'),
                ];
              }
            }
          }
        }

        setGenerationResults((prev) => ({...prev, ...newResults}));

        setGenerationFailures((prev) => {
          const next = {...prev};
          for (const key of Object.keys(newResults)) {
            delete next[key];
          }
          return {...next, ...newFailures};
        });

        props.refreshDocuments();

        const singleGeneratedDocument = getSingleGeneratedDocument(newResults, newFailures);

        if (isNotNil(singleGeneratedDocument)) {
          fileNotification.registerFile(singleGeneratedDocument.fileId, {
            onSuccess: () => {
              if (singleGeneratedDocument.url) {
                printPDF(singleGeneratedDocument.url);
              } else {
                showNotification.error();
              }
            },
          });
        }

        if (Object.keys(newFailures).length === 0) {
          showNotification.success(i18n.t('entity.order.notifications.success-generate-documents'));
        }
      })
      .catch(handleApiError);
  };

  const handleToggleSelectAll = isAllSelected ? handleUnselectAll : handleSelectAll;

  const isLoading = isAvailableDocumentsLoading || isRestrictionsLoading;
  const isError = isAvailableDocumentsError || isRestrictionsError;

  const generationErrorCount = Object.keys(generationFailures).length;
  const hasResults = Object.keys(generationResults).length > 0;

  return (
    <DataStatus isLoading={isLoading} isError={isError} minHeight={40}>
      <VStack spacing={4}>
        <Show when={generationErrorCount > 0}>
          <GenerationErrorBanner
            count={generationErrorCount}
            data-testid={suffixTestId('generationErrorBanner', props)}
          />
        </Show>

        <SelectAllHeader
          isAllSelected={isAllSelected}
          onToggle={handleToggleSelectAll}
          data-testid={suffixTestId('selectAllHeader', props)}
        />

        <VStack spacing={2}>
          {visibleDocuments.map((document) => {
            const result = generationResults[document.key];

            if (isNotNil(result)) {
              return (
                <GenerationResultRow
                  key={document.key}
                  titleKey={DOCUMENT_LABEL_MAP[result.key]}
                  documentKey={result.key}
                  url={result.url}
                  data-testid={suffixTestId(`resultRow-${result.key}`, props)}
                />
              );
            }

            return (
              <DocumentRow
                key={document.key}
                documentKey={document.key}
                titleKey={DOCUMENT_LABEL_MAP[document.key]}
                selection={getSelection(document.key)}
                restrictionMessage={
                  generationFailures[document.key] ??
                  getRestrictionMessage(restrictions, document.key)
                }
                templateOptions={getTemplateOptionsByLocale(document.key)}
                isDisabled={document.style === 'disabled'}
                onCheckboxChange={handleCheckboxChange}
                onTemplateChange={handleTemplateChange}
                data-testid={suffixTestId(`documentRow-${document.key}`, props)}
              />
            );
          })}
        </VStack>

        <HStack spacing={2} justify="flex-end">
          <Button
            variant="secondary"
            title={i18n.t(hasResults ? 'general.actions.close' : 'general.actions.cancel')}
            onClick={props.onClose}
            data-testid={suffixTestId('cancel', props)}
          />
          <Button
            variant="primary"
            title={i18n.t('entity.order.actions.generate')}
            isDisabled={!hasSelectedDocuments}
            isLoading={isBulkPrintLoading}
            onClick={handleGenerate}
            data-testid={suffixTestId('generate', props)}
          />
        </HStack>
      </VStack>
    </DataStatus>
  );
}
